import React, { useContext, useEffect, useState } from 'react';
import Navbar from './components/Navbar/Navbar';
import { SocketContext } from './utils/socket';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { loginRequest, msalInstance } from './authProvider';
import {
	CarrierContext,
	CategoryContext,
	RequestContext,
	TrailerLocationContext,
} from './utils/context';
import {
	getCarriers,
	getCategories,
	getRequests,
	getTrailerLocations,
	getTrailers,
} from './utils/api';
import RVAC from './Views/RVAC';
import RVAC2 from './Views/RVAC2';
import RMAN from './Views/RMAN';
import Requests from './Views/Requests';
import Search from './Views/Search';
import Admin from './Views/Admin';
import PrimaryLot from './Views/PrimaryLot';
import Container from './components/ui/Container';
import Error from './components/Modals/Error';
import OffSiteLot from './Views/OffSiteLot';
import axios from 'axios';
import AccessDenied from './Views/AccessDenied';

const App: React.FC = () => {
	const [data, setData] = useState({
		trailers: [],
		requests: [],
		carriers: [],
		categories: [],
		trailerLocations: [],
	});
	const [error, setError] = useState(false);
	const [warning, setWarning] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isMsalInitialized, setMsalInitialized] = useState(true);
	const [isAuthenticated, setIsAuthenticated] = useState(true);
	const navigate = useNavigate();

	useEffect(() => {
		const initializeMsal = async () => {
			try {
				await msalInstance.initialize();
				const response = await msalInstance.handleRedirectPromise();
				let account = null;
				if (response?.account) {
					msalInstance.setActiveAccount(response.account);
					account = response.account;
				}
				else {
					const accounts = msalInstance.getAllAccounts();
					if (accounts.length > 0) {
						msalInstance.setActiveAccount(accounts[0]);
						account = accounts[0];
						setIsAuthenticated(true);
					}
				}

				if (!account) {
					return msalInstance.loginRedirect({
						scopes: ["openid", "profile"],
					});
				}

				if (!account.idToken) {
					console.error("Login error:", error);
				}
				const res = await axios.post(
					`${process.env.REACT_APP_API_URL}authorize`,
					{},
					{
						headers: {
							Authorization: `Bearer ${account.idToken}`,
						},
					}
				);
				if (res.data?.authorize === true || res.data?.authorized === true) {
					setIsAuthenticated(true);
				} else {
					setIsAuthenticated(false);
					setMsalInitialized(false);
					navigate("/AccessDenied");
				}
			} catch (error: any) {
				if (error?.response?.status === 403) {
					setIsAuthenticated(false);
					setMsalInitialized(false);
					navigate("/AccessDenied");
				} else {
					console.error("Login error:", error);
				}
			}
		};

		initializeMsal();
	}, [navigate]);

	//REST
	useEffect(() => {
		const fetchData = async () => {
			const trailers = await getTrailers();
			const carriers = await getCarriers();
			const categories = await getCategories();
			const requests = await getRequests();
			const trailerLocations = await getTrailerLocations();

			setData({
				trailers,
				requests,
				carriers,
				categories,
				trailerLocations,
			});
		};
		fetchData();
	}, []);

	const io = useContext(SocketContext);

	useEffect(() => {
		io.on('returnTrailerAdded', (trailer) => {
			setData((oldState) => ({ ...oldState, trailers: trailer.trailers }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnTrailerDeleted', (trailer) => {
			setData((oldState) => ({ ...oldState, trailers: trailer.trailers }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnTrailerUpdated', (trailer) => {
			setData((oldState) => ({ ...oldState, trailers: trailer.trailers }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnRequestAdded', (request) => {
			setData((oldState) => ({ ...oldState, requests: request.requests }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnInRequest', (request) => {
			setData((oldState) => ({ ...oldState, requests: request.requests }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnCompleted', (request) => {
			setData((oldState) => ({
				...oldState,
				requests: request.requests,
				trailers: request.trailers,
			}));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnDeleteRequest', (request) => {
			setData((oldState) => ({ ...oldState, requests: request.requests }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnAddCarrier', (newCarrier) => {
			setData((oldState) => ({ ...oldState, carriers: newCarrier.carriers }));
		});
	}, [io]);
	useEffect(() => {
		io.on('returnDeleteCarrier', (newCarrier) => {
			setData((oldState) => ({ ...oldState, carriers: newCarrier.carriers }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnAddCategory', (newCategory) => {
			setData((oldState) => ({ ...oldState, categories: newCategory.categories }));
		});
	}, [io]);
	useEffect(() => {
		io.on('returnDeleteCategory', (newCategory) => {
			setData((oldState) => ({ ...oldState, categories: newCategory.categories }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnEditCategory', (newCategory) => {
			setData((oldState) => ({ ...oldState, categories: newCategory.categories }));
		});
	}, [io]);

	useEffect(() => {
		io.on('returnDeparted', (request) => {
			setData((oldData) => ({ ...oldData, trailers: request.trailers }));
		});
	});

	useEffect(() => {
		io.on('returnMove', (request) => {
			setData((oldData) => ({ ...oldData, trailers: request.trailers }));
		});
	});

	useEffect(() => {
		io.on('error', (res) => {
			setError(true);
			if (res.warning) {
				setWarning(true)
			}
			setErrorMessage(res.error);
		});
	}, [io]);

	return (
		<>
			<Routes>
				<Route path="/AccessDenied" element={<AccessDenied />} />
			</Routes>

			{isMsalInitialized ? (isAuthenticated ? (
				<div className="App flex flex-col h-screen">
					<Navbar />
					{error ? <Error open={error} warning={warning} close={() => { setError(false); setWarning(false) }} error={errorMessage} /> : null}
					<RequestContext.Provider value={data.requests}>
						<CategoryContext.Provider value={data.categories}>
							<CarrierContext.Provider value={data.carriers}>
								<TrailerLocationContext.Provider value={data.trailerLocations}>
									<Routes>
										<Route path="/" element={<RVAC trailers={data.trailers} />} />
										<Route path="/rvac2" element={<RVAC2 trailers={data.trailers} />} />
										<Route path="/RMAN" element={<RMAN trailers={data.trailers} />} />
										<Route path="/Requests" element={<Requests trailers={data.trailers} />} />
										<Route path="/Search" element={<Search />} />
										<Route path="/Admin" element={<Admin />} />
										<Route path="/PrimaryLot" element={<PrimaryLot trailers={data.trailers} />} />
										<Route path="/OffSiteLot" element={<OffSiteLot trailers={data.trailers} />} />
									</Routes>
								</TrailerLocationContext.Provider>
							</CarrierContext.Provider>
						</CategoryContext.Provider>
					</RequestContext.Provider>
				</div>
			) : <div></div>) : <div></div>};
		</>
	)
}

export default App;

